import { GuidelineCategories } from 'consts/hiToolsConsts'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  AlignersTxTracking as AlignersTxTrackingIcon,
  BracesTxTracking as BracesTxTrackingIcon,
  Elastics as ElasticsIcon,
  IPR as IPRIcon,
  OralHygiene as OralHygieneIcon,
  Other as OtherIcon,
  RPE as RPEIcon,
  TxGoals as TxGoalsIcon
} from 'components/common/icons'
import AlignersTxTracking from './Categories/AlignersTxTracking'
import BracesTxTracking from './Categories/BracesTxTracking'
import TxGoals from './Categories/TxGoals'
import Elastics from './Categories/Elastics'
import IPR from './Categories/IPR'
import RPE from './Categories/RPE'
import { isEmpty } from 'lodash'
import { sortTeeth } from 'utils/giUtils'

export default () => {
  const { t } = useTranslation()

  const { data: patientBriefData, loadingStatus } = useSelector(state => state.patientsReducer.patientBrief)
  const practiceMembers = useSelector(state => state.profileReducer.practiceMembers.data)

  const latestPatientBrief = useMemo(
    () => ({
      ...patientBriefData?.[0],
      categories: JSON.parse(patientBriefData?.[0]?.categories || '{}')
    }),
    [patientBriefData]
  )

  const lastUpdatedBy = useMemo(
    () => practiceMembers?.find(pm => pm.id === latestPatientBrief?.creatorDoctorId)?.name,
    [latestPatientBrief, practiceMembers]
  )

  const hasPatientBriefLoaded = useMemo(
    () => !!patientBriefData && !isEmpty(latestPatientBrief?.categories),
    [latestPatientBrief?.categories, patientBriefData]
  )

  const summarizeUntrackedTeeth = useCallback(
    untrackedTeeth =>
      !untrackedTeeth?.length
        ? ''
        : t('dialogs.hiPatientGuidelinesViewer.categories.summary.untrackedTeeth', {
            untrackedTeeth: sortTeeth(untrackedTeeth).join(', ')
          }),
    [t]
  )

  const summarizeAttachments = useCallback(
    attachments =>
      !attachments?.length
        ? ''
        : t('dialogs.hiPatientGuidelinesViewer.categories.summary.attachments', {
            untrackedTeeth: sortTeeth(attachments).join(', ')
          }),
    [t]
  )

  const summarizElastics = useCallback(
    (elastics = []) =>
      !elastics.length
        ? ''
        : elastics
            .map(
              (elastic, i) =>
                `#${i + 1}: ${sortTeeth(Object.keys(elastic.selections))
                  .map(tooth => `${tooth}`)
                  .join(', ')}`
            )
            .join(', '),
    []
  )

  const summarizeTxGoals = useCallback(
    (txGoals = {}) =>
      Object.keys(txGoals)
        .filter(key => txGoals[key]?.isChecked)
        .map(txGoalKey => t(`dialogs.hiPatientGuidelinesViewer.items.${txGoalKey}`))
        .join(', '),
    [t]
  )

  const alignersCategorySummary = useMemo(() => {
    const categoryItems = latestPatientBrief?.categories?.[GuidelineCategories.AlignersTxTracking]?.items
    const untrackedTeeth = summarizeUntrackedTeeth(categoryItems?.untrackedTeeth)
    const attachments = summarizeAttachments(categoryItems?.attachments)
    return untrackedTeeth || attachments
  }, [latestPatientBrief, summarizeUntrackedTeeth, summarizeAttachments])

  const bracesCategorySummary = useMemo(() => {
    const categoryItems = latestPatientBrief?.categories?.[GuidelineCategories.BracesTxTracking]?.items
    const wireSequence = categoryItems?.wireSequence
    if (wireSequence) {
      return t('dialogs.hiPatientGuidelinesViewer.categories.summary.wireSequence', {
        wireSequence
      })
    }

    const untrackedTeeth = summarizeUntrackedTeeth(categoryItems?.untrackedTeeth)
    return untrackedTeeth
  }, [latestPatientBrief, summarizeUntrackedTeeth, t])

  const hasPatientBrief = useMemo(() => !isEmpty(latestPatientBrief.categories), [latestPatientBrief.categories])
  const categories = useMemo(
    () =>
      [
        {
          key: GuidelineCategories.AlignersTxTracking,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.alignersTxTracking.title'),
          icon: <AlignersTxTrackingIcon />,
          component: AlignersTxTracking,
          summary: alignersCategorySummary
        },
        {
          key: GuidelineCategories.BracesTxTracking,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.bracesTxTracking.title'),
          icon: <BracesTxTrackingIcon />,
          component: BracesTxTracking,
          summary: bracesCategorySummary
        },
        {
          key: GuidelineCategories.TxGoals,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.txGoals.title'),
          icon: <TxGoalsIcon />,
          component: TxGoals,
          summary: summarizeTxGoals(latestPatientBrief?.categories?.[GuidelineCategories.TxGoals]?.items)
        },
        {
          key: GuidelineCategories.Elastics,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.elastics.title'),
          icon: <ElasticsIcon />,
          component: Elastics,
          summary: summarizElastics(latestPatientBrief?.categories?.[GuidelineCategories.Elastics]?.items?.elastics)
        },
        {
          key: GuidelineCategories.IPR,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.IPR.title'),
          icon: <IPRIcon />,
          component: IPR,
          summary: t('dialogs.hiPatientGuidelinesViewer.categories.summary.alignerNumber', {
            alignerNumber: latestPatientBrief?.categories?.[GuidelineCategories.IPR]?.items?.alignerNumberReached
          })
        },
        {
          key: GuidelineCategories.RPE,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.RPE.title'),
          icon: <RPEIcon />,
          component: RPE,
          summary: t('dialogs.hiPatientGuidelinesViewer.categories.summary.numberOfTurns', {
            numberOfTurns: latestPatientBrief?.categories?.[GuidelineCategories.RPE]?.items?.numberOfTurns
          })
        },
        {
          key: GuidelineCategories.OralHygiene,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.oralHygiene.title'),
          icon: <OralHygieneIcon />,
          component: () => null,
          summary: ''
        },
        {
          key: GuidelineCategories.OtherAppliancesAndGuidelines,
          title: t('dialogs.hiPatientGuidelinesViewer.categories.otherAppliancesAndGuidelines.title'),
          icon: <OtherIcon />,
          component: () => null,
          summary: latestPatientBrief?.categories?.[GuidelineCategories.OtherAppliancesAndGuidelines]?.note
        }
      ]
        .filter(category => latestPatientBrief?.categories?.[category.key]?.isEnabled)
        .map(category => ({
          ...category,
          summary: category.summary || latestPatientBrief?.categories?.[category.key]?.note // Fallback to note if summary is empty
        })),
    [latestPatientBrief, t, alignersCategorySummary, bracesCategorySummary, summarizElastics, summarizeTxGoals]
  )

  return {
    hasPatientBriefLoaded,
    hasPatientBrief,
    latestPatientBrief,
    categories,
    lastUpdatedBy,
    loadingStatus
  }
}
